import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { SovReportAggregate } from "../../model/maia/sov-upload";

type Presentation = "auto" | "hbar" | "column" | "donut" | "table";

/** The current section config, emitted to the parent so the export reflects on-screen edits. */
export interface MaiaChartConfig {
    dimension: string;
    measure: string;
    aggregation: string;
    presentation: string;
}

const CHART_COLOR = "#2563eb";
const MAX_ITEMS = 12;

/**
 * One configurable report section: pick a dimension / measure / aggregation / presentation and it
 * groups the job's SOV rows and renders a horizontal bar / column / donut (ECharts) with an
 * "At a glance" line and an optional data table. Changing any control re-fetches and re-renders.
 */
@Component({
    selector: "maia-report-chart",
    templateUrl: "./maia-report-chart.component.html",
    styleUrls: ["./maia-report-chart.component.scss"],
})
export class MaiaReportChartComponent implements OnInit {
    @Input() datasetId: string | null = null;
    @Input() jobId: number | null = null;
    @Input() title = "";
    @Input() category = "";
    @Input() source = "";

    /** Column options for the dropdowns (non-numeric = dimensions, numeric = measures). */
    @Input() dimensions: string[] = [];
    @Input() measures: string[] = [];

    // Initial config (also the section's defaults).
    @Input() dimension = "";
    @Input() measure = "Total Insured Value";
    @Input() aggregation = "sum";
    @Input() presentation: Presentation = "auto";

    /** Emits the current config so the parent keeps its section list in sync (used by the export). */
    @Output() readonly configChange = new EventEmitter<MaiaChartConfig>();

    selectedDimension = "";
    selectedMeasure = "";
    selectedAggregation = "sum";
    selectedPresentation: Presentation = "auto";
    includeTable = true;

    data: SovReportAggregate | null = null;
    loading = false;
    chartOptions: any = null;

    readonly aggregations = [
        { value: "sum", label: "Sum" },
        { value: "avg", label: "Average" },
        { value: "count", label: "Count" },
    ];
    readonly presentations: Array<{ value: Presentation; label: string }> = [
        { value: "auto", label: "Auto (recommended)" },
        { value: "hbar", label: "Horizontal bars" },
        { value: "column", label: "Columns" },
        { value: "donut", label: "Donut" },
        { value: "table", label: "Table only" },
    ];

    constructor(private readonly maiaUploadService: MaiaUploadService) {}

    ngOnInit(): void {
        this.selectedDimension = this.dimension;
        this.selectedMeasure = this.measure;
        this.selectedAggregation = this.aggregation;
        this.selectedPresentation = this.presentation;
        this.load();
    }

    onConfigChange(): void {
        this.emitConfig();
        this.load();
    }

    /** Presentation change only re-renders from the data already loaded (no refetch). */
    onPresentationChange(): void {
        this.emitConfig();
        this.chartOptions = this.data && this.data.items.length ? this.buildChart(this.data) : null;
    }

    private emitConfig(): void {
        this.configChange.emit({
            dimension: this.selectedDimension,
            measure: this.selectedMeasure,
            aggregation: this.selectedAggregation,
            presentation: this.selectedPresentation,
        });
    }

    load(): void {
        if (!this.datasetId || this.jobId == null || !this.selectedDimension) {
            this.data = null;
            this.chartOptions = null;
            return;
        }
        this.loading = true;
        this.maiaUploadService
            .getReportAggregate(this.datasetId, this.jobId, {
                dimension: this.selectedDimension,
                measure: this.selectedMeasure,
                aggregation: this.selectedAggregation,
            })
            .subscribe(
                (data) => {
                    this.data = data;
                    this.chartOptions = data.items.length ? this.buildChart(data) : null;
                    this.loading = false;
                },
                () => {
                    this.loading = false;
                }
            );
    }

    get items() {
        return this.data ? this.data.items.slice(0, MAX_ITEMS) : [];
    }

    /** Resolve "auto" to a concrete chart form from the data shape. */
    get form(): Exclude<Presentation, "auto"> {
        if (this.selectedPresentation !== "auto") {
            return this.selectedPresentation;
        }
        const n = this.data ? this.data.items.length : 0;
        if (n <= 5) {
            return "donut";
        }
        return n <= 10 ? "column" : "hbar";
    }

    get showChart(): boolean {
        return this.form !== "table" && !!this.chartOptions && this.items.length > 0;
    }

    get showTable(): boolean {
        return this.includeTable || this.form === "table";
    }

    get atAGlance(): string {
        if (!this.data || !this.data.items.length) {
            return "";
        }
        const top = this.data.items[0];
        const share = this.data.total > 0
            ? ` · ${Math.round((top.value / this.data.total) * 100)}% of the section total`
            : "";
        return `Largest category: ${top.label} at ${this.fmt(top.value)}${share}. ${this.data.items.length} of ${this.data.locations.toLocaleString()} locations represented.`;
    }

    fmt(value: number): string {
        return this.data && this.data.money ? this.formatMoney(value) : Math.round(value).toLocaleString();
    }

    share(value: number): string {
        if (!this.data || this.data.total <= 0 || this.data.aggregation === "avg") {
            return "-";
        }
        return `${Math.round((value / this.data.total) * 100)}%`;
    }

    private buildChart(data: SovReportAggregate): any {
        const items = data.items.slice(0, MAX_ITEMS);
        const labels = items.map((i) => i.label);
        const values = items.map((i) => i.value);
        const money = data.money;
        const axisFmt = (v: number) => (money ? this.formatMoney(v) : v.toLocaleString());
        const form = this.form;

        // A donut of all-zero values renders misleading equal slices (ECharts fills a placeholder
        // pie when the total is 0) - show no chart instead. Bars/columns render an honest empty axis.
        if (form === "donut" && values.reduce((sum, v) => sum + v, 0) <= 0) {
            return null;
        }

        if (form === "donut") {
            return {
                tooltip: { trigger: "item" },
                legend: { bottom: 0, type: "scroll" },
                series: [
                    {
                        type: "pie",
                        radius: ["55%", "78%"],
                        avoidLabelOverlap: true,
                        label: { show: false },
                        data: items.map((i) => ({ name: i.label, value: i.value })),
                    },
                ],
            };
        }

        if (form === "column") {
            return {
                grid: { left: 8, right: 16, top: 16, bottom: 24, containLabel: true },
                tooltip: { trigger: "axis", valueFormatter: axisFmt },
                xAxis: { type: "category", data: labels, axisLabel: { interval: 0, rotate: labels.length > 6 ? 30 : 0 } },
                yAxis: { type: "value", axisLabel: { formatter: axisFmt } },
                series: [{ type: "bar", data: values, itemStyle: { color: CHART_COLOR }, barMaxWidth: 48 }],
            };
        }

        // horizontal bars — largest at top
        return {
            grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
            tooltip: { trigger: "axis", valueFormatter: axisFmt },
            xAxis: { type: "value", axisLabel: { formatter: axisFmt } },
            yAxis: { type: "category", data: [...labels].reverse() },
            series: [{ type: "bar", data: [...values].reverse(), itemStyle: { color: CHART_COLOR }, barMaxWidth: 22 }],
        };
    }

    private formatMoney(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e9) {
            return `$${(value / 1e9).toFixed(2)}B`;
        }
        if (abs >= 1e6) {
            return `$${(value / 1e6).toFixed(1)}M`;
        }
        if (abs >= 1e3) {
            return `$${(value / 1e3).toFixed(1)}K`;
        }
        return `$${Math.round(value).toLocaleString()}`;
    }
}
